import React from "react";
import { authorizedFetch } from "./boot.js";
import { Card } from "./ui.jsx";

// Lot contacts — the people who own or have an interest in a vendor's horses
// (VD-SPEC-LAUNCH-044). This panel records them and says which horses each one
// stands behind. It sends nothing and offers no way to: VD-SPEC-LAUNCH-051
// keeps that a person's act, so there is deliberately no send control, no
// mailto link and no address in any URL this panel builds.
//
// Self-contained on purpose. People reuses this vendor-scoped registry rather
// than creating another contact record beside it.

async function request(path, input, key) {
  const response = await authorizedFetch(path, input ? {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ input, idempotency_key: key }),
  } : {});
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(body?.error?.code || "request_failed");
    error.status = response.status;
    throw error;
  }
  return body;
}

const trimmed = value => (value || "").trim();

export function LotContacts({ sale = "26R2R", vendor = window.VD.access.realm,
  accessByContact = {}, onInvite = null }) {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [pending, setPending] = React.useState(null);
  const [refreshRequired, setRefreshRequired] = React.useState(false);
  const [form, setForm] = React.useState(null);
  const [horses, setHorses] = React.useState(null);
  const [confirming, setConfirming] = React.useState(null);
  const mounted = React.useRef(true);
  const busyRef = React.useRef(false);
  const readPath = `/collections/vendor.contacts.get@1/?${new URLSearchParams({ sale, vendor })}`;

  React.useEffect(() => {
    mounted.current = true;
    let active = true;
    request(readPath).then(value => { if (active) { setData(value); setLoading(false); } })
      .catch(() => { if (active) { setError("Your contacts could not be loaded. Refresh to try again."); setLoading(false); } });
    return () => { active = false; mounted.current = false; };
  }, [readPath]);

  function closeAll() {
    setForm(null); setHorses(null); setConfirming(null);
  }

  async function refresh() {
    if (busyRef.current) return;
    setLoading(true); setError(""); closeAll();
    try {
      const value = await request(readPath);
      if (!mounted.current) return;
      setData(value); setRefreshRequired(false);
    } catch (_) {
      if (mounted.current) setError("Your contacts could not be loaded. Refresh before making another change.");
    } finally { if (mounted.current) setLoading(false); }
  }

  // One save path for all three Actions, so a refused contact, a refused link
  // and a refused removal cannot drift into telling a vendor different things.
  async function send(attempt) {
    if (busyRef.current) return;
    busyRef.current = true; setBusy(true); setError(""); setNotice(attempt.saving);
    try {
      await request(attempt.path, attempt.input, attempt.key);
      if (!mounted.current) return;
      setPending(null); setRefreshRequired(true); closeAll();
      try {
        const value = await request(readPath);
        if (!mounted.current) return;
        setData(value); setRefreshRequired(false); setNotice(attempt.said);
      } catch (_) {
        if (mounted.current) setError("The change was saved, but your contacts could not be loaded. Refresh before making another change.");
      }
    } catch (failure) {
      if (!mounted.current) return;
      setNotice("");
      if ([400, 404, 409, 412].includes(failure.status)) {
        setPending(null); setRefreshRequired(true); closeAll();
        setError(failure.status === 412
          ? "This contact changed somewhere else. Refresh and review before saving again."
          : "This change could not be accepted. Refresh and check the contact and its horses.");
      } else {
        setPending(attempt);
        setError("The save could not be confirmed. Retry this same save before making another change.");
      }
    } finally {
      busyRef.current = false;
      if (mounted.current) setBusy(false);
    }
  }

  const lots = data?.lots || [];
  const contacts = data?.contacts || [];
  const lotNumber = new Map(lots.map(lot => [lot.id, lot.number]));
  const locked = busy || loading || refreshRequired || Boolean(pending) || data?.canManage !== true;

  function saveContact(event) {
    event.preventDefault();
    if (locked || !form) return;
    const name = trimmed(form.name);
    const email = trimmed(form.email);
    if (!name || !email) { setError("A contact needs a name and an email address."); return; }
    const identified = form.id
      ? { contact_id: form.id, expected_revision: form.revision }
      : {};
    send({
      path: "/actions/vendor.contacts.save@1/",
      input: { ...identified, name, email, mobile: trimmed(form.mobile) || null },
      key: window.crypto.randomUUID(),
      saving: form.id ? `Saving ${name}…` : `Adding ${name}…`,
      said: form.id ? `${name} is saved.` : `${name} is one of your contacts.`,
    });
  }

  function saveHorses() {
    if (locked || !horses) return;
    send({
      path: "/actions/vendor.contacts.set_lots@1/",
      input: {
        sale,
        contact_id: horses.id,
        lot_ids: lots.filter(lot => horses.chosen.includes(lot.id)).map(lot => lot.id),
        expected_revision: horses.revision,
      },
      key: window.crypto.randomUUID(),
      saving: `Saving which horses ${horses.name} is a contact for…`,
      said: `${horses.name}'s horses are saved.`,
    });
  }

  function removeContact() {
    if (locked || !confirming) return;
    send({
      path: "/actions/vendor.contacts.remove@1/",
      input: { contact_id: confirming.id, expected_revision: confirming.revision },
      key: window.crypto.randomUUID(),
      saving: `Removing ${confirming.name}…`,
      said: `${confirming.name} is no longer one of your contacts.`,
    });
  }

  function contactForm() {
    return <form className="contact-form" onSubmit={saveContact}>
      <h4>{form.id ? `Change ${form.was}` : "New contact"}</h4>
      <label>Name
        <input name="name" type="text" maxLength={200} value={form.name}
          onChange={event => setForm({ ...form, name: event.target.value })}/>
      </label>
      <label>Email
        <input name="email" type="email" maxLength={254} value={form.email}
          onChange={event => setForm({ ...form, email: event.target.value })}/>
      </label>
      <label>Mobile <span className="muted">(optional)</span>
        <input name="mobile" type="tel" maxLength={32} value={form.mobile}
          onChange={event => setForm({ ...form, mobile: event.target.value })}/>
      </label>
      <div className="row" style={{ gap: 10 }}>
        <button type="submit" className="btn sm primary" disabled={busy}>Save contact</button>
        <button type="button" className="btn sm" onClick={closeAll}>Cancel</button>
      </div>
    </form>;
  }

  function horsePicker() {
    const toggle = id => setHorses({
      ...horses,
      chosen: horses.chosen.includes(id)
        ? horses.chosen.filter(item => item !== id)
        : [...horses.chosen, id],
    });
    return <div className="contact-horses" aria-label={`Horses ${horses.name} is a contact for`}>
      <h4>Which horses is {horses.name} a contact for?</h4>
      {lots.map(lot => <label key={lot.id} className="contact-horse">
        <input type="checkbox" data-lot={lot.id} checked={horses.chosen.includes(lot.id)}
          onChange={() => toggle(lot.id)}/>
        <span>Lot {lot.number}{lot.withdrawn ? " · Withdrawn" : ""}</span>
      </label>)}
      {!lots.length && <p className="caption">This vendor has no reviewed lots in this sale.</p>}
      <div className="row" style={{ gap: 10 }}>
        <button type="button" className="btn sm primary" disabled={busy} onClick={saveHorses}>Save horses</button>
        <button type="button" className="btn sm" onClick={closeAll}>Cancel</button>
      </div>
    </div>;
  }

  function row(contact) {
    const linked = contact.lots.map(id => `Lot ${lotNumber.get(id) || id}`);
    const accessState = accessByContact[contact.id];
    const dashboardAccess = typeof accessState === "string" ? accessState : accessState?.label;
    const canInviteContact = Boolean(onInvite && (!accessState || accessState.canReinvite === true));
    return <li key={contact.id} className="contact-row">
      <div className="contact-person">
        <strong>{contact.name}</strong>
        <span className="muted">{contact.email}</span>
        {contact.mobile && <span className="muted">{contact.mobile}</span>}
        {dashboardAccess && <span className="contact-access">{dashboardAccess}</span>}
      </div>
      <p className="caption">{linked.length ? linked.join(", ") : "No horses chosen yet"}</p>
      {!locked && <div className="contact-actions">
        <button type="button" className="btn sm" onClick={() => {
          closeAll();
          setForm({
            id: contact.id, revision: contact.revision, was: contact.name,
            name: contact.name, email: contact.email, mobile: contact.mobile || "",
          });
        }}>Change</button>
        <button type="button" className="btn sm" onClick={() => {
          closeAll();
          setHorses({
            id: contact.id, revision: contact.revision, name: contact.name,
            chosen: [...contact.lots],
          });
        }}>Choose horses</button>
        <button type="button" className="btn sm" onClick={() => {
          closeAll();
          setConfirming({ id: contact.id, revision: contact.revision, name: contact.name });
        }}>Remove</button>
        {canInviteContact && <button type="button" className="btn sm primary" onClick={event => {
          closeAll(); onInvite(contact, event.currentTarget);
        }}>{accessState?.canReinvite ? "Reinvite to dashboard" : "Invite to dashboard"}</button>}
      </div>}
      {form?.id === contact.id && contactForm()}
      {horses?.id === contact.id && horsePicker()}
      {confirming?.id === contact.id && <div className="contact-confirm" role="alertdialog"
        aria-label={`Remove ${contact.name}`}>
        <p>Remove {contact.name} from your contacts? Their horses stay where they are.</p>
        <div className="row" style={{ gap: 10 }}>
          <button type="button" className="btn sm primary" disabled={busy} onClick={removeContact}>Yes, remove</button>
          <button type="button" className="btn sm" onClick={closeAll}>Keep them</button>
        </div>
      </div>}
    </li>;
  }

  return <Card title="Lot contacts" subtitle={`${data?.vendorName || vendor} · ${sale}`}>
    <p className="muted">
      The people who own or have an interest in your horses. Recording someone here
      does not contact them: nothing is sent unless someone at your business sends it.
    </p>
    <div className="row" style={{ gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
      <button className="btn sm" disabled={busy || loading || Boolean(pending)} onClick={refresh}>Refresh contacts</button>
      {!locked && <button type="button" className="btn sm primary" onClick={() => {
        closeAll();
        setForm({ id: null, revision: null, was: "", name: "", email: "", mobile: "" });
      }}>Add a contact</button>}
      {pending && <button className="btn sm primary" disabled={busy} onClick={() => send(pending)}>Retry same save</button>}
    </div>
    <p role="status" aria-live="polite" className="muted">{notice}</p>
    {error && <p role="alert" className="error-text">{error}</p>}
    {loading && <p role="status">Loading your contacts…</p>}
    {data && <>
      {form && form.id === null && contactForm()}
      {contacts.length === 0
        ? <p>You have no contacts for this sale yet.</p>
        : <ul className="contact-list">{contacts.map(row)}</ul>}
    </>}
  </Card>;
}
